import { useState, useEffect, useCallback } from "react";
import {
  HiOutlineCurrencyDollar, HiOutlineCalendar, HiOutlineUserGroup, HiOutlineReceiptPercent,
  HiOutlineMagnifyingGlass, HiOutlineArrowDownTray, HiOutlineXMark,
  HiOutlineEye, HiOutlineEllipsisVertical, HiOutlineChevronDown,
  HiOutlineChevronLeft, HiOutlineChevronRight,
  HiOutlineArrowTrendingUp, HiOutlineArrowTrendingDown,
  HiOutlineEnvelope, HiOutlinePencilSquare, HiOutlineArrowUturnLeft,
} from "react-icons/hi2";
import AdminLayout from "./AdminLayout";
import { donationsAPI } from "../services/api";
import toast from "react-hot-toast";
import "./AdminDonations.css";

const CAMPAIGNS = ["All Campaigns","General Fund","Retreat Support","Sponsor a Veteran","Building Fund"];
const STATUSES  = ["All Statuses","Completed","Pending","Refunded"];
const STATUS_CLS = { Completed:"adn__badge--completed", Pending:"adn__badge--pending", Refunded:"adn__badge--refunded" };

const pct = (curr, prev) => {
  if (prev == null || prev === 0) return null;
  const d = ((curr - prev) / prev) * 100;
  return { value: Math.abs(d).toFixed(1), up: d >= 0 };
};

const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US",{ year:"numeric", month:"short", day:"numeric" }) : "—";
const fmtAmt = (v) => parseFloat(v || 0).toLocaleString("en-US",{ minimumFractionDigits:2, maximumFractionDigits:2 });

/* Row context menu */
function RowMenu({ don, onView, onUpdateStatus }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position:"relative" }}>
      <button type="button" className="a-btn a-btn--ghost aap__action-btn"
        onClick={e => { e.stopPropagation(); setOpen(v => !v); }}>
        <HiOutlineEllipsisVertical />
      </button>
      {open && (
        <>
          <div style={{ position:"fixed",inset:0,zIndex:49 }} onClick={() => setOpen(false)} />
          <div className="asu__row-menu">
            <button onClick={() => { onView(don); setOpen(false); }}><HiOutlineEye /> View Details</button>
            <button onClick={() => { onUpdateStatus(don.id, "Completed"); setOpen(false); }}>✓ Mark Completed</button>
            <button onClick={() => { onUpdateStatus(don.id, "Refunded"); setOpen(false); }}>
              <HiOutlineArrowUturnLeft /> Refund
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function DetailPanel({ don, onClose, onUpdateStatus }) {
  const [addingNote, setAddingNote] = useState(false);
  const [noteText, setNoteText]     = useState("");
  return (
    <div className="adn__detail">
      <div className="adn__detail-head">
        <h3 className="adn__detail-title">Donation Details</h3>
        <button className="adn__detail-close" onClick={onClose}><HiOutlineXMark /></button>
      </div>

      <div className="adn__detail-user">
        <div className="adn__detail-avatar" style={{ background:"#b85c38" }}>
          {(don.name || don.donor || "?")[0].toUpperCase()}
        </div>
        <div>
          <p className="adn__detail-name">{don.name || don.donor || "Anonymous"}</p>
          <p className="adn__detail-email">{don.email || "—"}</p>
        </div>
      </div>

      <div className="adn__detail-amount-block">
        <p className="adn__detail-amount">
          ${fmtAmt(don.amount)} <span>{don.currency || "USD"}</span>
        </p>
        <span className={`adn__badge ${STATUS_CLS[don.payment_status || don.status] || ""}`}>
          {don.payment_status || don.status}
        </span>
        <p className="adn__detail-date">{fmt(don.created_at || don.date)}</p>
        {don.transaction_id && <p className="adn__detail-txid">Transaction ID: {don.transaction_id}</p>}
      </div>

      <div className="adn__detail-rows">
        {[
          ["Campaign",        don.campaign],
          ["Payment Method",  don.payment_method || "—"],
          ["Receipt ID",      don.receipt_number || don.receipt || "—"],
          ["Notes",           don.notes || "—"],
        ].map(([k,v]) => (
          <div key={k} className="adn__detail-row">
            <span className="adn__detail-key">{k}</span>
            <span className="adn__detail-val">{v}</span>
          </div>
        ))}
      </div>

      <div className="adn__detail-actions">
        <p className="adn__detail-section-title">Actions</p>
        <a href={`mailto:${don.email || ""}?subject=Your Forge Donation Receipt`}
          className="a-btn a-btn--outline adn__action-full">
          <HiOutlineEnvelope /> Resend Receipt
        </a>

        {!addingNote ? (
          <button type="button" className="a-btn a-btn--outline adn__action-full"
            onClick={() => setAddingNote(true)}>
            <HiOutlinePencilSquare /> Add Note
          </button>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:"0.5rem" }}>
            <textarea
              className="aap__notes-input" rows={3}
              placeholder="Add a note…" value={noteText}
              onChange={e => setNoteText(e.target.value)} />
            <div style={{ display:"flex", gap:"0.5rem" }}>
              <button type="button" className="a-btn a-btn--rust" style={{ flex:1 }}
                onClick={() => { toast.success("Note saved."); setAddingNote(false); setNoteText(""); }}>
                Save
              </button>
              <button type="button" className="a-btn a-btn--outline"
                onClick={() => { setAddingNote(false); setNoteText(""); }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {(don.payment_status || don.status) !== "Refunded" && (
          <button type="button" className="adn__refund-btn"
            onClick={() => {
              toast((t) => (
                <div style={{ display:"flex", flexDirection:"column", gap:"0.5rem" }}>
                  <p style={{ margin:0, fontWeight:700 }}>Refund this donation?</p>
                  <p style={{ margin:0, fontSize:"0.78rem", color:"#6b6258" }}>This action cannot be undone.</p>
                  <div style={{ display:"flex", gap:"0.5rem" }}>
                    <button onClick={() => { toast.dismiss(t.id); onUpdateStatus(don.id, "Refunded"); }}
                      style={{ background:"#dc2626", color:"#fff", border:"none", borderRadius:"5px", padding:"0.35rem 0.75rem", cursor:"pointer", fontWeight:700, fontSize:"0.78rem" }}>
                      Confirm Refund
                    </button>
                    <button onClick={() => toast.dismiss(t.id)}
                      style={{ background:"#f3f4f0", border:"none", borderRadius:"5px", padding:"0.35rem 0.75rem", cursor:"pointer", fontSize:"0.78rem" }}>
                      Cancel
                    </button>
                  </div>
                </div>
              ), { duration: Infinity, style: { maxWidth:"340px" } });
            }}>
            <HiOutlineArrowUturnLeft /> Refund Donation
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminDonations() {
  const [search, setSearch]       = useState("");
  const [campaign, setCampaign]   = useState("All Campaigns");
  const [status, setStatus]       = useState("All Statuses");
  const [dateFrom, setDateFrom]   = useState("");
  const [dateTo, setDateTo]       = useState("");
  const [showDate, setShowDate]   = useState(false);
  const [selected, setSelected]   = useState(null);
  const [donations, setDonations] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [kpis, setKpis]           = useState({});
  const [loading, setLoading]     = useState(true);
  const [page, setPage]           = useState(1);

  const fetchDonations = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit:10 };
      if (search)                      params.search    = search;
      if (campaign !== "All Campaigns")params.campaign  = campaign;
      if (status   !== "All Statuses") params.status    = status;
      if (dateFrom)                    params.date_from = dateFrom;
      if (dateTo)                      params.date_to   = dateTo;
      const res = await donationsAPI.list(params);
      setDonations(res.data || []);
      setTotalCount(res.pagination?.total || 0);
      if (res.kpis) setKpis(res.kpis);
      setChecked(new Set()); setSelectAll(false);
    } catch (err) { console.error(err.message); }
    finally { setLoading(false); }
  }, [search, campaign, status, dateFrom, dateTo, page]);

  useEffect(() => { fetchDonations(); }, [fetchDonations]);

  const handleUpdateStatus = async (id, newStatus) => {
    const tid = toast.loading("Updating donation status…");
    try {
      await donationsAPI.updateStatus(id, newStatus);
      fetchDonations();
      setSelected(prev => prev?.id === id ? { ...prev, payment_status: newStatus, status: newStatus } : prev);
      toast.success(`Donation marked as ${newStatus}.`, { id: tid });
    } catch (err) { toast.error("Failed: " + err.message, { id: tid }); }
  };

  const [exportLoading, setExportLoading] = useState(false);
  const [checked, setChecked]           = useState(new Set());
  const [selectAll, setSelectAll]       = useState(false);

  const toggleSelectAll = () => {
    if (selectAll) { setChecked(new Set()); setSelectAll(false); }
    else { setChecked(new Set(donations.map(d => d.id))); setSelectAll(true); }
  };

  const toggleCheck = (id) => {
    setChecked(prev => {
      const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id);
      setSelectAll(n.size === donations.length); return n;
    });
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const ids = checked.size > 0 ? [...checked] : [];
      await donationsAPI.exportCsv(ids);
      const msg = ids.length > 0
        ? `Exported ${ids.length} donation${ids.length>1?"s":""}.`
        : "Exported all donations.";
      toast.success(msg);
    } catch (err) { toast.error("Export failed: " + err.message); }
    finally { setExportLoading(false); }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / 10));

  const totalRaised   = kpis.total_raised   ?? donations.reduce((s,d) => s + parseFloat(d.amount||0), 0);
  const monthlyTotal  = kpis.month_total    ?? 0;
  const donCount      = kpis.count          ?? totalCount;
  const avgDon        = kpis.average        ?? (donCount > 0 ? totalRaised / donCount : 0);

  const DON_STATS = [
    { label:"Total Raised",    value:"$"+parseFloat(totalRaised).toLocaleString(), trend:pct(kpis.total_raised, kpis.prev_total_raised),    Icon:HiOutlineCurrencyDollar, color:"#e05a2b", bg:"rgba(var(--admin-rust-rgb), 0.12)"  },
    { label:"This Month",      value:"$"+parseFloat(monthlyTotal).toLocaleString(), trend:pct(kpis.month_total,kpis.prev_month_total),       Icon:HiOutlineCalendar,       color:"#16a34a", bg:"rgba(22,163,74,0.12)"  },
    { label:"Total Donations", value:donCount.toLocaleString(), trend:pct(kpis.count, kpis.prev_count),                                     Icon:HiOutlineUserGroup,      color:"#7c3aed", bg:"rgba(124,58,237,0.12)" },
    { label:"Avg Donation",    value:"$"+parseFloat(avgDon).toFixed(0), trend:pct(kpis.average, kpis.prev_average),                          Icon:HiOutlineReceiptPercent, color:"#d97706", bg:"rgba(217,119,6,0.12)"  },
  ];

  return (
    <AdminLayout pageTitle="Donations" pageSubtitle="Track and manage all donations and contributions." permission="donations.view">
      {/* Stats */}
      <div className="adn__stats-grid">
        {DON_STATS.map(s => (
          <div key={s.label} className="acard adn__stat-card">
            <div className="adn__stat-top">
              <div className="adn__stat-icon" style={{ background:s.bg }}>
                <s.Icon style={{ color:s.color, fontSize:"1.1rem" }} />
              </div>
              <p className="adn__stat-label">{s.label}</p>
            </div>
            <p className="adn__stat-value">{s.value}</p>
            {s.trend ? (
              <p className={`adn__stat-change ${s.trend.up?"up":"down"}`}>
                {s.trend.up ? <HiOutlineArrowTrendingUp /> : <HiOutlineArrowTrendingDown />}
                {s.trend.value}% vs last period
              </p>
            ) : (
              <p className="adn__stat-change" style={{ color:"#9ca3af" }}>No comparison yet</p>
            )}
          </div>
        ))}
      </div>

      <div className={`adn__layout ${selected ? "adn__layout--detail" : ""}`}>
        <div className="adn__table-panel">
          {/* Bulk action bar */}
          {checked.size > 0 && (
            <div className="abulk__bar">
              <span className="abulk__count">{checked.size} selected</span>
              <button type="button" className="a-btn a-btn--outline abulk__btn"
                onClick={handleExport} disabled={exportLoading}>
                <HiOutlineArrowDownTray /> {exportLoading ? "Exporting…" : "Export Selected"}
              </button>
              <button type="button" className="a-btn a-btn--ghost abulk__btn"
                onClick={() => { setChecked(new Set()); setSelectAll(false); }}>
                Clear
              </button>
            </div>
          )}

          {/* Filters */}
          <div className="adn__filters">
            <div className="asu__search-wrap">
              <HiOutlineMagnifyingGlass className="asu__search-icon" />
              <input type="text" className="asu__search" placeholder="Search donors..."
                value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>

            {/* Date */}
            <div className="aap__date-wrap">
              <button type="button" className="asu__date-filter" onClick={() => setShowDate(v=>!v)}>
                <HiOutlineCalendar />
                <span>{dateFrom ? `${dateFrom}${dateTo?` → ${dateTo}`:""}` : "All Dates"}</span>
                <HiOutlineChevronDown />
              </button>
              {showDate && (
                <div className="aap__date-dropdown">
                  <div className="aap__date-picker">
                    <div className="aap__date-field"><label>From</label><input type="date" value={dateFrom} onChange={e=>{setDateFrom(e.target.value);setPage(1);}} /></div>
                    <div className="aap__date-field"><label>To</label><input type="date" value={dateTo} onChange={e=>{setDateTo(e.target.value);setPage(1);}} /></div>
                    <button type="button" className="a-btn a-btn--outline" style={{ fontSize:"0.75rem", padding:"0.35rem 0.6rem" }}
                      onClick={()=>{setDateFrom("");setDateTo("");setPage(1);setShowDate(false);}}>Clear</button>
                  </div>
                </div>
              )}
            </div>

            <div className="aap__select-wrap">
              <select className="aap__filter-select" value={campaign} onChange={e=>{setCampaign(e.target.value);setPage(1);}}>
                {CAMPAIGNS.map(c => <option key={c}>{c}</option>)}
              </select>
              <HiOutlineChevronDown className="aap__select-icon" />
            </div>

            <div className="aap__select-wrap">
              <select className="aap__filter-select" value={status} onChange={e=>{setStatus(e.target.value);setPage(1);}}>
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
              <HiOutlineChevronDown className="aap__select-icon" />
            </div>

            <div className="asu__filters-right">
              <button type="button" className="a-btn a-btn--outline" onClick={handleExport} disabled={exportLoading}>
                <HiOutlineArrowDownTray /> {exportLoading ? "Exporting…" : checked.size > 0 ? `Export ${checked.size}` : "Export All"}
              </button>
            </div>
          </div>

          <div className="acard adn__table-card">
            <table className="adn__table">
              <thead>
                <tr>
                  <th><input type="checkbox" className="aap__checkbox" checked={selectAll} onChange={toggleSelectAll} /></th>
                  <th>Donor</th>
                  <th>Amount</th>
                  <th>Campaign</th>
                  <th>Date ↓</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="7" style={{ textAlign:"center", padding:"3rem", color:"#9ca3af" }}>Loading…</td></tr>
                ) : donations.length === 0 ? (
                  <tr><td colSpan="7" style={{ textAlign:"center", padding:"3rem", color:"#9ca3af" }}>No donations found.</td></tr>
                ) : donations.map(d => (
                  <tr key={d.id}
                    className={`aap__row ${selected?.id===d.id?"aap__row--selected":""}`}
                    onClick={() => setSelected(d)}>
                    <td onClick={e=>e.stopPropagation()}><input type="checkbox" className="aap__checkbox" checked={checked.has(d.id)} onChange={()=>toggleCheck(d.id)} /></td>
                    <td>
                      <div className="aap__applicant">
                        <div className="aap__applicant-avatar" style={{ background:"#b85c38" }}>
                          {(d.name || d.donor || "A")[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="aap__applicant-name">{d.name || d.donor || "Anonymous"}</p>
                          <p className="aap__applicant-email">{d.email || "—"}</p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <p className="adn__amount-cell">${fmtAmt(d.amount)}</p>
                      <p className="adn__currency-cell">{d.currency || "USD"}</p>
                    </td>
                    <td className="adn__campaign-cell">{d.campaign}</td>
                    <td>
                      <p className="aap__applicant-name">{fmt(d.created_at || d.date)}</p>
                    </td>
                    <td>
                      <span className={`adn__badge ${STATUS_CLS[d.payment_status || d.status] || ""}`}>
                        {d.payment_status || d.status}
                      </span>
                    </td>
                    <td onClick={e=>e.stopPropagation()}>
                      <div className="aap__row-actions">
                        <button type="button" className="a-btn a-btn--ghost aap__action-btn"
                          onClick={() => setSelected(d)}><HiOutlineEye /></button>
                        <RowMenu don={d} onView={() => setSelected(d)} onUpdateStatus={handleUpdateStatus} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="aap__pagination">
              <p className="aap__pagination-info">
                {totalCount > 0 ? `Showing ${((page-1)*10)+1}–${Math.min(page*10,totalCount)} of ${totalCount} donations` : "No results"}
              </p>
              <div className="aap__pagination-pages">
                <button type="button" className="aap__page-btn" disabled={page===1} onClick={()=>setPage(p=>p-1)}><HiOutlineChevronLeft /></button>
                {Array.from({length:Math.min(5,totalPages)},(_,i)=>i+1).map(p=>(
                  <button key={p} type="button" className={`aap__page-btn ${page===p?"aap__page-btn--active":""}`} onClick={()=>setPage(p)}>{p}</button>
                ))}
                <button type="button" className="aap__page-btn" disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)}><HiOutlineChevronRight /></button>
              </div>
              <div className="aap__select-wrap">
                <select className="aap__page-size"><option>10 / page</option><option>20 / page</option></select>
                <HiOutlineChevronDown className="aap__select-icon" />
              </div>
            </div>
          </div>
        </div>

        {selected && (
          <DetailPanel
            don={selected}
            onClose={() => setSelected(null)}
            onUpdateStatus={handleUpdateStatus}
          />
        )}
      </div>
    </AdminLayout>
  );
}
